import React from 'react';
import { arrayMove, SortableContainer, SortableElement } from "react-sortable-hoc";
import { Component, Container, Position, PuzzleButton, VBox } from "puzzle";
import { FILTER } from "../../Dashboard/Filters/Types";
import AdvancedSearchListItem from "./AdvancedSearchListItem";
import classNames from "classnames";
import _ from "lodash";
import { getFavoritesFilterCount, pingForFavoritesFilterCount } from "../../util/Util";

type PROPS = {}
type STATE = {
  filters: FILTER[],
  selectedSearchId: number | null
}

const SortableItem = SortableElement(({ value, onItemClick, selectedSearchId }: any) => <AdvancedSearchListItem
  currentItem={value} onItemClick={onItemClick} selectedSearchId={selectedSearchId} />);

const SortableList = SortableContainer((props: any) => {
  const { items, onItemClick, selectedSearchId } = props;
  const listClass = classNames(
    'tp-landing-page-filters-content tp-100',
    {
      'k-justify-content-start': items.length,
      'k-justify-content-center': !items.length
    }
  )
  return (
    <ul className={listClass} aria-label={props.ariaLabel}
    >
      {items.length ?
        <>
          {!!items.length && items.map((value: any, index: any) => (
            <SortableItem key={`item-${value.filterId}`} index={index} value={value} onItemClick={onItemClick}
                          selectedSearchId={selectedSearchId} />
          ))}
        </> :
        <VBox
          alignItemsCenter
          justifyContentCenter
          gap={4}
          className={'tp-filter-list-empty'}>

          <span className={'no-documents'} />
          <span className={'tp-color-light-LM tp-filter-list-empty-text'}>{props.emptyFilterListLabel}</span>
        </VBox>

      }
    </ul>
  );
});

class AdvancedSearchList extends Component<PROPS, STATE> {
  savedSearchesRef: any;
  preventFirstSearchSelection = false; //To prevent selection on each load. Once loaded first search should be preselected and on every other load/reload should not
  state: STATE = {
    filters: [],
    selectedSearchId: null
  };
  favoritesFilterCountIds: number[] = [];
  favoriteFilterUrl: string = '/jobPhase/filters/count';
  favoritesFilterCountProperty: string = 'jobPhaseCount';
  /**
   * Prevent filter update state when component is unmounted
   */
  isComponentMounted: boolean = false;

  moduleDidMount = () => {
    this.isComponentMounted = true;
  };

  /**
   * Component will unmount
   * - stop pending ping for submission count
   */
  componentWillUnmount(): void {
    this.isComponentMounted = false;
    this.dispatch('stopPingingRequest', Position.end);
    super.componentWillUnmount();
  }

  createFilter = () => {
    this.dispatch('openAdvancedSearchDialog', '', { modeData: { mode: 'create' } })
  };

  /**
   * Loads saved searches
   *
   * @param filters
   */
  loadSearchList = (filters: FILTER[]) => {
    this.favoritesFilterCountIds = _.filter(filters, (filterItem: any) => !filterItem.jobPhaseCount).map((filterItem: any) => filterItem.filterId);
    getFavoritesFilterCount(this);
    this.setState({ filters }, () => {
      if (this.savedSearchesRef && !this.preventFirstSearchSelection) {
        let firstFilter = filters[0];
        if (firstFilter) {
          this.onItemClick(firstFilter);
        }
        this.preventFirstSearchSelection = true;
      }
    });
  };

  /**
   * Sets selectedSearchId of selected search to state
   *
   * @param currentItem
   */
  onItemClick = (currentItem: FILTER) => {
    this.dispatch('checkboxSelectionClear');
    this.setState({
      selectedSearchId: currentItem.filterId || 0
    })
  };

  onSortEnd = ({ oldIndex, newIndex }: any) => {
    const filters = arrayMove(this.state.filters, oldIndex, newIndex);
    this.setState({ filters });
    const params = filters.map(({ filterName }: FILTER, index: number) => {
      return { index, filterName }
    });
    this.dispatch('saveSearchesSort', '', { params })
  };

  pingForFavoritesFilterCount = (responseData: any) => pingForFavoritesFilterCount(responseData, this);

  /**
   * Removes selection from saved searches
   */
  removeSavedSearchesSelection = () => {
    this.setState({
      selectedSearchId: null
    })
  };

  render() {
    const { filters, selectedSearchId } = this.state;
    return (
      <>
        <Container
          alignItemsCenter
          className={'tp-filter-list-header'}
          displayFlex
          justifyContentBetween
          htmlTag={'span'}
        >
          <h3 className={'tp-font-size-md tp-text-semibold'}>{this.translate('savedSearches')}</h3>
          <PuzzleButton
            aria-label={this.translate('addFilter')}
            titleText={this.translate('addFilter')}
            fillMode="flat"
            themeColor={'primary'}
            iconClass={"tp-icon tp-add"}
            onClick={this.createFilter}
            tooltipPosition={'right'}
            withTooltip
          />
        </Container>
        <Container
          alignItemsCenter
          ref={(el: any) => this.savedSearchesRef = el?.element || el}
          vBox
          className={'tp-filter-list'}
        >
          <SortableList
            ariaLabel={this.translate('SRL-savedSearchesFilterList')}
            helperClass={'tp-drag-filter'}
            emptyFilterListLabel={this.translate('emptyFilerList')}
            items={filters}
            onItemClick={this.onItemClick}
            onSortEnd={this.onSortEnd}
            selectedSearchId={selectedSearchId}
            axis='y'
            distance={5}
          />
        </Container>
      </>
    );
  }
}

export default AdvancedSearchList;
